import { Injectable } from '@angular/core';
import { MapLayersManager } from '../map-layers-manager';
import { DataDrivenPropertyValueSpecification, LineLayerSpecification } from 'maplibre-gl';
import { TrackStyles } from '../../../../configuration/model/TrackStyles';
import { Subscription } from 'rxjs';
import { SettingsService, StatusLayerType } from '../../../../configuration/settings.service';
import { ConfigurationService } from '../../../../configuration/configuration.service';
import { RouteSegmentStatusSourceService } from './route-segment-status-source.service';
import { MapStyles } from '../../../../configuration/map-styles';
import { RoutesManagerService } from '../../../../data/routes/routes-manager.service';


@Injectable({
  providedIn: 'root',
})
export class RouteSegmentStatusLayerService {
  static readonly LAYER_ID_PREFIX = 'route-segment-status';

  private mapLayersManager: MapLayersManager;
  private lineLayers: LineLayerSpecification[] = [];

  private isEnabled: boolean;
  // Route Status theme is selected, display this layer
  private visibleBySettings = false;
  // hide this layer when Selected Route component is visible
  private componentVisibility = false;
  private trackStyles: TrackStyles;
  private highlightedRoute: { configId: number, routeId: number };

  private readonly openSubscriptions = Array<Subscription>();

  constructor(
    private readonly settingsService: SettingsService,
    private readonly configurationService: ConfigurationService,
    private readonly routesManager: RoutesManagerService
  ) {}

  init(mapLayersManager: MapLayersManager, isEnabled: boolean) {
    if (!!this.mapLayersManager) {
      throw Error('The map layers manager has already been set.');
    }
    this.mapLayersManager = mapLayersManager;
    this.isEnabled = isEnabled;
    this.trackStyles = this.configurationService.trackStyles;
    this.addLayers();

    if (this.isEnabled) {
      this.connectToManager();
    }
  }

  release() {
    if (!this.mapLayersManager) {
      throw Error('The map has not been set!');
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
    this.mapLayersManager = null;
  }

  private connectToManager() {
    // handle visibility from settings
    const settingsChangedSubscription = this.settingsService.settingsChangedObservable.subscribe(newSettings => {
      if (newSettings.key === SettingsService.TRACKS_LAYER_TYPE_KEY) {
        this.visibleBySettings = newSettings.value === StatusLayerType.ROUTE_STATUS;
        this.handleLayerVisibilityChange();
      }
    });
    this.openSubscriptions.push(settingsChangedSubscription);

    // handle style change
    const trackStylesSubscription = this.configurationService.userTrackStyles$.subscribe(styles => {
      this.handleStyleChange(styles);
    });
    this.openSubscriptions.push(trackStylesSubscription);

    // handle toggle Route component (selected Route from Left menu)
    const routesVisibilitySubscription = this.routesManager.basedOnRouteVisibility$.subscribe(visible => {
      this.componentVisibility = visible;
      this.handleLayerVisibilityChange();
    });
    this.openSubscriptions.push(routesVisibilitySubscription);
  }

  private handleLayerVisibilityChange() {
    this.lineLayers.forEach(lineLayer => {
      this.mapLayersManager.setLayerVisibility(lineLayer.id, this.getLayerVisibility());
    });
  }

  public getLayerVisibility(): boolean {
    return this.isEnabled && !this.componentVisibility && !!this.visibleBySettings;
  }

  private handleStyleChange(styles: TrackStyles) {
    this.trackStyles = styles;
    this.lineLayers.forEach(lineLayer => {
      const layerId = lineLayer.id;
      const trackStyle = this.getTrackStyle(layerId);
      this.mapLayersManager.setLayerPaintProperty(layerId, 'line-color', trackStyle.color);
      this.mapLayersManager.setLayerPaintProperty(layerId, 'line-opacity', trackStyle.opacity);
      this.mapLayersManager.setLayerPaintProperty(layerId, 'line-blur', trackStyle.blur);
      this.mapLayersManager.setLayerPaintProperty(
        layerId,
        'line-width',
        this.getWidthStyle() // ignoring trackStyle.width,
      );
      this.mapLayersManager.setLayerPaintProperty(
        layerId,
        'line-dasharray',
        !!trackStyle.dasharray ? ['literal', trackStyle.dasharray] : undefined
      );
    });
  }

  public highlightRoute(configId: number, routeId: number) {
    this.highlightedRoute = configId && routeId ? { configId, routeId } : null;
    this.updateStyle();
  }

  public addLayers() {
    const source = this.mapLayersManager.getGeoJsonSource(RouteSegmentStatusSourceService.ROUTE_SEGMENT_SOURCE_ID);
    if (source === undefined || source === null) {
      console.error('Route Segment Status Source is null. This should not happen!');
    }

    // add layers
    const tracksLayerType = this.settingsService.getStringValue(SettingsService.TRACKS_LAYER_TYPE_KEY);
    this.visibleBySettings = tracksLayerType === StatusLayerType.ROUTE_STATUS;
    const isVisible = this.getLayerVisibility() ? 'visible' : 'none';
    this.getLineLayers().forEach(layer => {
      layer.layout.visibility = isVisible;
      this.lineLayers.push(layer);
      this.mapLayersManager.addLayer(layer, this.mapLayersManager.firstSymbolLayerId);
    });
  }

  private getLineLayers(): LineLayerSpecification[] {
    const nowUnixSeconds = Math.floor(Date.now() / 1000);
    const nowMinutes = nowUnixSeconds / 60;
    return [0, 1, 2, 3].map(layerNumber => {
      const layerId = `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-${layerNumber}`;
      const trackStyle = this.getTrackStyle(layerId);
      return {
        id: layerId,
        type: 'line',
        source: RouteSegmentStatusSourceService.ROUTE_SEGMENT_SOURCE_ID,
        filter: this.getLayerFilterExpression(layerNumber, nowMinutes),
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': this.getColorStyle(trackStyle.color),
          'line-opacity': trackStyle.opacity,
          'line-blur': trackStyle.blur,
          'line-width': this.getWidthStyle(), // ignoring trackStyle.width,
          'line-dasharray': ['literal', !!trackStyle.dasharray ? trackStyle.dasharray : [10]],
        },
      } as LineLayerSpecification;
    });
  }

  private updateStyle() {
    [0, 1, 2, 3].forEach(layerNumber => {
      const layerId = `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-${layerNumber}`;
      const trackStyle = this.getTrackStyle(layerId);
      this.mapLayersManager.setLayerPaintProperty(layerId, 'line-color', this.getColorStyle(trackStyle.color));
    });
  }

  private getTrackStyle(layerId: string) {
    switch (layerId) {
      case `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-0`:
        return this.trackStyles.routeStatusNotDone;
      case `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-1`:
        return this.trackStyles.routeStatusLevel1;
      case `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-2`:
        return this.trackStyles.routeStatusLevel2;
      case `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-3`:
        return this.trackStyles.routeStatusLevel3;
      default:
        console.error(`Getting Track Style for non-existent Layer ID: ${layerId}!`);
        return null;
    }
  }

  private getLayerFilterExpression(layerNumber: number, nowMinutes: number) {
    const lastEntered = ['get', 'last_entered'];
    const hasLastEntered = ['all', ['has', 'last_entered'], ['!=', lastEntered, null]];
    const lastEntryInMinutesSinceNowExpr = ['-', nowMinutes, ['/', lastEntered, 60]];

    switch (layerNumber) {
      case 1: // 0–2h
        return ['case', hasLastEntered, ['<=', lastEntryInMinutesSinceNowExpr, 120], false];
      case 2: // 2h–5h
        return [
          'case',
          hasLastEntered,
          ['all', ['>', lastEntryInMinutesSinceNowExpr, 120], ['<=', lastEntryInMinutesSinceNowExpr, 300]],
          false,
        ];
      case 3: // 5h–12h
        return [
          'case',
          hasLastEntered,
          ['all', ['>', lastEntryInMinutesSinceNowExpr, 300], ['<=', lastEntryInMinutesSinceNowExpr, 720]],
          false,
        ];
      case 0: // >12h, OR null/undefined
      default:
        return ['case', hasLastEntered, ['>', lastEntryInMinutesSinceNowExpr, 720], true];
    }
  }

  private getColorStyle(defaultColor: string): DataDrivenPropertyValueSpecification<string> {
    if (!!this.highlightedRoute && !!this.highlightedRoute.configId && !!this.highlightedRoute.routeId) {
      const colorExpression: any[] = ['case'];
      colorExpression.push(['==', ['get', 'routeid'], this.highlightedRoute.routeId]);
      colorExpression.push(MapStyles.PAUSED_COLOR);
      colorExpression.push(defaultColor);
      return colorExpression as DataDrivenPropertyValueSpecification<string>;
    } else {
      return defaultColor;
    }
  }

  private getWidthStyle(): DataDrivenPropertyValueSpecification<number> {
    const expr = MapStyles.ROUTE_STATUS_WIDTH;
    return [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      10,
      ['*', expr, ['^', 2, -1]],
      24,
      ['*', expr, ['^', 2, 8]],
    ] as DataDrivenPropertyValueSpecification<number>;
  }
}
